import Cookies from 'js-cookie';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import type { IBannerInfo } from '@/interface/IBannerInfo';
import { BannerAction, GameFrom, PromotionBannerType, RoutePage, SirenSSOLocation } from '@/interface/enum';
import TeraApi from '@/libraries/apis/teraApi';
import { platform } from '@/libraries/helpers/platformHelper';
import { getHost, getUrl, setUrlParam } from '@/libraries/url';
import { useRootStore } from '@/stores/rootStore';

export function useUmaiBanner() {
    const route = useRoute();

    async function executeBannerAction(bannerInfo: IBannerInfo) {
        const { isAsi } = storeToRefs(useRootStore());
        switch (bannerInfo.action) {
            case BannerAction.InJoiOpenGame: {
                await openGame(bannerInfo);
                break;
            }
            case BannerAction.InJoiEventPromotion: {
                await openInjoiPromotion(bannerInfo);
                break;
            }
            case BannerAction.ProductSwitch: {
                await productSwitch(bannerInfo);
                break;
            }

            case BannerAction.CasinoInGame:
            case BannerAction.CasinoToGameLobby:
            case BannerAction.CasinoDealerPage:
            {
                bannerInfo.actionDetail = `Casino_${bannerInfo.actionDetail}`;
                await productSwitch(bannerInfo);
                break;
            }

            case BannerAction.OpenWindow: {
                openWindow(bannerInfo);
                break;
            }

            case BannerAction.RedirectTo: {
                redirectUrl(bannerInfo);
                break;
            }

            case BannerAction.RouteTo: {
                const currentPath = route.path;
                changeRoute(bannerInfo, currentPath);
                break;
            }

            case BannerAction.ToSportLeague: {
                goToLeagueZone(bannerInfo);
                break;
            }

            default:
                console.error(`BannerAction ${bannerInfo.action} has not been implemented.`);
                break;
        }

        async function productSwitch(bannerInfo: IBannerInfo): Promise<void> {
            if (!isAsi.value) {
                if (!bannerInfo.actionDetail) {
                    return;
                }
                const [product] = bannerInfo.actionDetail.split('_');
                const productType = convertUmaiProduct(product);

                if (productType === 'Games') {
                    Cookies.set('GameFrom', GameFrom[bannerInfo.gameFrom], { domain: getHost() });
                }

                platform.showLogin(productType);
            }

            if (!bannerInfo.actionDetail) {
                throw new Error('actionDetail is required for productSwitch action');
            }

            const [product, page] = bannerInfo.actionDetail.split('_');
            const productType = convertUmaiProduct(product);

            const { data: { landingUrl } } = await TeraApi.productSwitch({
                product: productType,
                gameFrom: bannerInfo.gameFrom.toString(),
            });

            if (product === 'Siren' && page) {
                const sirenPageDictionary: Partial<Record<string, PromotionBannerType>> = {
                    SportsCupPromotion: PromotionBannerType.SportsCupPromotion,
                    SportsPromotion: PromotionBannerType.SportsPromotion,
                    IplLuckyDip: PromotionBannerType.IplLuckyDip,
                    DailyMission: PromotionBannerType.DailyMission,
                    PlatformPromotion: PromotionBannerType.PlatformPromotion,
                };
                let url = landingUrl;
                url = setUrlParam(url, 'location', SirenSSOLocation.SpecificPromotionPage);
                url = setUrlParam(url, 'promotiontype', sirenPageDictionary[page] ?? '');
                window.location.href = url;
                return;
            }

            if (product === 'Casino' && page) {
                let url = landingUrl;
                url = setUrlParam(url, 'ActionType', convertCasinoEnumType(bannerInfo.action));
                url = setUrlParam(url, 'ActionDetail', bannerInfo.remark ? `${page};${bannerInfo.remark?.promotionCode}` : '');
                window.location.href = url;
                return;
            }

            window.location.href = landingUrl;
        }

        async function openGame(bannerInfo: IBannerInfo) {
            if (!isAsi.value) {
                platform.showLogin('Sports');
                return;
            }

            const { actionDetail: gameCode, gameFrom } = bannerInfo;
            if (!gameCode) return;
            const { data: { landingUrl } } = await TeraApi.openInJoiGame(parseInt(gameCode), gameFrom);
            window.location.href = landingUrl;
        }
    }

    const router = useRouter();

    function goToLeagueZone(bannerInfo: IBannerInfo) {
    /**
     * Expected
     * bannerInfo.actionDetail = "football?leagueNames=abc,def"
     * or
     * bannerInfo.actionDetail = "football?leagueIds=123,456"
    */
        const [sportRouteName, queryString] = bannerInfo.actionDetail?.split('?') ?? [];

        const query = Object.fromEntries(new URLSearchParams(queryString));
        if (!query.leagueNames && !query.leagueIds) return;
        router.push({ name: RoutePage.MainBySport, params: { sportRouteName }, query });
    }

    function changeRoute(bannerInfo: IBannerInfo, currentPath: string) {
        const path = bannerInfo.actionDetail;
        if (path && currentPath !== path) {
            router.push(path);
        }
    }

    return {
        executeBannerAction,
    };
}

function convertCasinoEnumType(action: BannerAction): string {
    const CasinoEnumMapping: Partial<Record<BannerAction, string>> = {
        [BannerAction.CasinoInGame]: '20',
        [BannerAction.CasinoToGameLobby]: '21',
        [BannerAction.CasinoDealerPage]: '23',
    };

    return CasinoEnumMapping[action] ?? '0';
}

function convertUmaiProduct(product: string) {
    if (product === 'Keno') return 'SBOKeno';
    if (product === 'Siren') return 'Promotion';

    return product;
}

function openWindow(bannerInfo: IBannerInfo) {
    if (!bannerInfo.actionDetail) {
        throw new Error('actionDetail is required for openWindow action');
    }

    const url = getUrl(bannerInfo.actionDetail);
    window.open(url, '_blank');
}

function redirectUrl(bannerInfo: IBannerInfo) {
    if (!bannerInfo.actionDetail) {
        throw new Error('actionDetail is required for redirectTo action');
    }

    const schema = window.location.protocol.replace(':', '');
    const url = bannerInfo.actionDetail.replace('{schema}', schema)
        .replace('{host}', getHost());

    window.location.href = url;
}

async function openInjoiPromotion(bannerInfo: IBannerInfo) {
    if (!bannerInfo.actionDetail) {
        throw new Error('actionDetail is required for openInjoiPromotion action');
    }

    const { data: { landingUrl } } = await TeraApi.productSwitch({
        product: 'Games',
        gameFrom: GameFrom.SportMobileTopBannerPromotion.toString(),
    });

    let url = landingUrl;
    url = setUrlParam(url, 'promotion', bannerInfo.actionDetail);
    window.open(url, '_blank');
}
